import {
  BASE_CII_LIMITS,
  CiiChartData,
  CiiDataPoint,
  CreateDryDockingResult,
  DashboardBreakdownBucket,
  DateRangeBucketKey,
  GetDashboardDataResponse,
} from '@navstation-portal/reporting/data-model/crew-dashboard';
import {
  AreaRangeGraphData,
  DateValueIndex,
  LimitData,
  LineGraphData,
  MapPoint,
  PlotlineConfigInput,
  ScatterGraphData,
  TooltipConfigInput,
} from '@navstation-portal/shared/data-model/charts';
import { roundToPlaces } from '@navstation-portal/shared/util/math';
import { getMax, getMin } from '@navstation-portal/shared/util/math';
import { DryDocking } from '@navstation-portal/vessels/data-model/vessel';
import * as Highcharts from 'highcharts';

type DashboardBreakdownByDateRange = Record<DateRangeBucketKey, DashboardBreakdownBucket>;

export function buildCrewDashboardVm(
  data: GetDashboardDataResponse | undefined,
  selectedRange: DateRangeBucketKey = '6_month'
) {
  if (!data)
    return {
      mileageGaugeData: [],
      co2EmissionsGaugeData: [],
      propulsionEfficiencyData: [],
      ciiRating: undefined,
      speedPerformance: [],
      propulsionEfficiency: [],
      meSfoc: [],
      dryDockingPlotLines: [],
      attainedCiiYMarker: [],
      attainedCiiLineData: [],
      attainedCIIAreaData: [],
    };

  const trafficSystemColours = {
    green: '#24cf7f',
    yellow: '#ffd942',
    red: '#d61024',
    grey: '#777777',
  };

  const getColourForValue = (value: number | undefined, limits: number[], reverseColours = false): string => {
    if (value === undefined || !Number.isFinite(value) || !value) {
      return trafficSystemColours.green;
    }

    const absoluteValue = Math.abs(value);

    const firstUpperLimit = limits[0];
    const secondUpperLimit = limits[1];

    if (!Number.isFinite(firstUpperLimit) || !Number.isFinite(secondUpperLimit)) {
      return trafficSystemColours.green;
    }

    const baseColors: [string, string, string] = reverseColours
      ? [trafficSystemColours.green, trafficSystemColours.yellow, trafficSystemColours.red]
      : [trafficSystemColours.red, trafficSystemColours.yellow, trafficSystemColours.green];

    const colors = baseColors;

    if (absoluteValue < firstUpperLimit) {
      return colors[0];
    }

    if (absoluteValue <= secondUpperLimit) {
      return colors[1];
    }

    return colors[2];
  };

  const createSolidGaugeData = (
    value: number | undefined,
    colourValue: number | undefined,
    limits: number[],
    reverseColours = false
  ): Highcharts.SeriesSolidgaugeOptions[] => {
    return [
      {
        type: 'solidgauge',
        data: [
          {
            y: value,
            color: getColourForValue(colourValue, limits, reverseColours),
          },
        ],
      },
    ];
  };

  const mileageGaugeData = createSolidGaugeData(
    data.breakdown[selectedRange]?.mileage,
    data.breakdown[selectedRange]?.fuel_deviation,
    [5, 15],
    true
  );

  const co2EmissionsGaugeData = createSolidGaugeData(
    data.breakdown[selectedRange]?.co2_emissions,
    data.breakdown[selectedRange]?.fuel_deviation,
    [5, 15],
    true
  );

  const speedPerformanceGaugeData = createSolidGaugeData(
    data.breakdown[selectedRange]?.speed_performance,
    data.breakdown[selectedRange]?.speed_performance,
    [80, 90]
  );

  const propulsionEfficiencyData = createSolidGaugeData(
    data.breakdown[selectedRange]?.propulsion_efficiency,
    data.breakdown[selectedRange]?.propulsion_efficiency,
    [85, 95]
  );

  const { breakdown, dryDockings } = data;
  const currentRange: DateRangeBucketKey = selectedRange;
  const dryDockingResult = createDryDockingPlotlines(dryDockings);
  const ddPlotLines = dryDockingResult?.plotlines;
  const scatter = createSpeedPerformanceData(breakdown, dryDockingResult, currentRange);
  const propdata = createPropulsionEfficiencyData(breakdown, currentRange);
  const sfoc = createSfocData(breakdown, currentRange);
  const ciiData = createCiiData(breakdown, currentRange);
  const currentCiiData = ciiData[currentRange];
  const currentBucket = breakdown[currentRange];

  return {
    co2EmissionsGaugeData,
    speedPerformanceGaugeData,
    propulsionEfficiencyData,
    mileageGaugeData,
    ciiRating: currentBucket?.cii_rating,
    speedPerformance: scatter[currentRange],
    propulsionEfficiency: propdata[currentRange],
    meSfoc: formatSeriesData(sfoc[currentRange]),
    dryDockingPlotLines: ddPlotLines,
    attainedCiiYMarker: currentCiiData?.yMarkers,
    attainedCiiLineData: currentCiiData?.ciiLifetimeData,
    attainedCIIAreaData: currentCiiData?.ciiAreaData,
  };
}

function getBucketKeys(data: DashboardBreakdownByDateRange, selectedRange?: DateRangeBucketKey): string[] {
  if (selectedRange) {
    return [selectedRange];
  }

  return Object.keys(data);
}

function createCiiData(
  data: DashboardBreakdownByDateRange,
  selectedRange?: DateRangeBucketKey
): Record<string, CiiChartData> {
  const result: Record<string, CiiChartData> = {};

  const toDateValueIndex = (series: [number, string | number | boolean | undefined][]): DateValueIndex => {
    const numericEntries = series.filter((entry): entry is [number, number] => {
      const value = entry[1];
      return typeof value === 'number' && Number.isFinite(value);
    });

    return Object.fromEntries(numericEntries);
  };

  getBucketKeys(data, selectedRange).forEach((key) => {
    const bucketData = data[key as keyof typeof data];
    if (
      !bucketData ||
      bucketData.attained_cii === undefined ||
      !bucketData.cii_required_lifetime ||
      !bucketData.cii_attained_lifetime ||
      !bucketData.co2_emissions_lifetime ||
      !bucketData.total_obs_distance_lifetime ||
      !bucketData.cii_limit_1 ||
      !bucketData.cii_limit_2 ||
      !bucketData.cii_limit_3 ||
      !bucketData.cii_limit_4
    ) {
      return;
    }

    const datesArray: number[] = bucketData.cii_required_lifetime.map((a) => a[0]);

    const ciiRating: number = bucketData.attained_cii;
    const ciiRequired: DateValueIndex = toDateValueIndex(bucketData.cii_required_lifetime);
    const ciiAttained: DateValueIndex = toDateValueIndex(bucketData.cii_attained_lifetime);
    const co2Emissions: DateValueIndex = toDateValueIndex(bucketData.co2_emissions_lifetime);
    const totalObsDistance: DateValueIndex = toDateValueIndex(bucketData.total_obs_distance_lifetime);
    const ciiLimit1: DateValueIndex = toDateValueIndex(bucketData.cii_limit_1);
    const ciiLimit2: DateValueIndex = toDateValueIndex(bucketData.cii_limit_2);
    const ciiLimit3: DateValueIndex = toDateValueIndex(bucketData.cii_limit_3);
    const ciiLimit4: DateValueIndex = toDateValueIndex(bucketData.cii_limit_4);
    const ciiLimits: DateValueIndex[] = [ciiLimit1, ciiLimit2, ciiLimit3, ciiLimit4];

    function getDataObjectArray() {
      return datesArray
        .map((date) => ({
          date,
          attained: ciiAttained[date],
          required: ciiRequired[date],
          average: ciiRating,
          co2: co2Emissions[date],
          distance: totalObsDistance[date],
        }))
        .filter(
          (point): point is CiiDataPoint =>
            Number.isFinite(point.attained) &&
            Number.isFinite(point.required) &&
            Number.isFinite(point.average) &&
            Number.isFinite(point.co2) &&
            Number.isFinite(point.distance)
        );
    }

    function buildDynamicBandSeries(): AreaRangeGraphData {
      const data = getDataObjectArray();
      const result: AreaRangeGraphData = BASE_CII_LIMITS.map((series) => ({
        ...series,
        data: [] as number[][],
      }));
      const { ciiMin, ciiMax } = getMinMaxAttainedCii(ciiAttained);

      data.forEach((item) => {
        const { date } = item;

        const { limits, minBoundary, maxBoundary } = gatherBoundaryData(
          ciiLimits,
          ciiMin,
          ciiMax,
          item.date,
          item.average
        );
        result[0].data.push([date, minBoundary, limits.top]);
        result[1].data.push([date, limits.top, limits.lowerMid]);
        result[2].data.push([date, limits.lowerMid, limits.upperMid]);
        result[3].data.push([date, limits.upperMid, limits.bottom]);
        result[4].data.push([date, limits.bottom, maxBoundary]);
      });
      return result;
    }

    function gatherCiiAttainedLifetimeData() {
      return getDataObjectArray().map((a) => [a.date, a.attained, a.required, a.average, a.co2, a.distance]);
    }

    const yMarkers: PlotlineConfigInput = [{ value: ciiRating, label: 'Average CII', colour: 'white', isDate: false }];

    const ciiLifetimeData: LineGraphData = [
      {
        name: 'Attained CII',
        data: gatherCiiAttainedLifetimeData(),
        keys: ['x', 'y', 'average', 'required', 'co2_emissions', 'distance'],
        type: 'line',
        zIndex: 10,
      },
    ];

    result[key] = {
      yMarkers,
      ciiLifetimeData,
      ciiAreaData: buildDynamicBandSeries(),
    };
  });

  return result;
}

function formatSeriesData(data: ScatterGraphData): ScatterGraphData {
  return data.map((series) => {
    const fuelGrade =
      typeof series.data[0]?.['fuelGrade'] === 'string' ? series.data[0]['fuelGrade'] : 'Unknown Fuel Grade';
    const name = fuelGrade.toUpperCase() + ' ' + series.name;
    return {
      ...series,
      name: name,
      tooltipConfig: getTooltipConfig(fuelGrade),
      data: series.data.map((point) => {
        const difference = roundToPlaces((point['meSfocLcvCorr'] as number) - (point['meModelSfoc'] as number), 2);
        return {
          ...point,
          difference: difference,
        };
      }),
    };
  });
}

function getTooltipConfig(fuelType: string): TooltipConfigInput {
  const fuelGradeLabel = `${fuelType.toUpperCase()} ME SFOC LCV Corr. Dev. (%)`;
  return [
    { key: 'reportNumber', label: 'Report Number' },
    { key: 'meSfocLcvCorr', label: 'ME SFOC LCV Corr. (g/kWh)' },
    { key: 'meModelSfoc', label: 'ME Model SFOC (g/kWh)' },
    { key: 'difference', label: 'Difference (g/kWh)' },
    { key: 'y', label: fuelGradeLabel },
    { key: 'tankName', label: 'Tank' },
    { key: 'bunkerDate', label: 'Bunker Date' },
    { key: 'bunkerPort', label: 'Bunker Port' },
  ];
}

function createDryDockingPlotlines(dryDocking: DryDocking[] | undefined): CreateDryDockingResult | undefined {
  if (!dryDocking) return;

  const toEpochMs = (rawDate: unknown): number | undefined => {
    if (typeof rawDate === 'number') {
      return Number.isFinite(rawDate) ? rawDate : undefined;
    }

    if (typeof rawDate === 'string') {
      const parsed = Date.parse(rawDate);
      return Number.isNaN(parsed) ? undefined : parsed;
    }

    if (rawDate && typeof rawDate === 'object' && '$date' in (rawDate as Record<string, unknown>)) {
      return toEpochMs((rawDate as { $date: unknown }).$date);
    }

    return undefined;
  };

  const items = dryDocking
    .filter((docking) => {
      if (docking.hull_cleaning || docking.propeller_polishing) {
        return true;
      }
      return false;
    })
    .reduce<CreateDryDockingResult['items']>((acc, docking) => {
      const dockingDate = toEpochMs(docking.date);
      if (dockingDate === undefined) {
        return acc;
      }

      const label = docking.hull_cleaning ? 'Hull Cleaning' : 'Propeller Polishing';
      acc.push({
        plotline: {
          value: dockingDate,
          label,
          isDate: true,
        },
        meta: {
          isPropellerPolish: docking.propeller_polishing,
          speedPerformanceBefore: docking.speed_performance_before,
          speedPerformanceAfter: docking.speed_performance_after,
          speedPerformanceChange: docking.speed_performance_change,
          speedPerformanceBeforeDays: docking.speed_performance_before_days,
          speedPerformanceAfterDays: docking.speed_performance_after_days,
          speedPerformanceBeforeReports: docking.speed_performance_before_reports,
          speedPerformanceAfterReports: docking.speed_performance_after_reports,
        },
      });

      return acc;
    }, []);

  return {
    items,
    plotlines: items.map((item) => item.plotline),
  };
}

function createPropulsionEfficiencyData(data: DashboardBreakdownByDateRange, selectedRange?: DateRangeBucketKey) {
  const result: Record<string, ScatterGraphData> = {};
  getBucketKeys(data, selectedRange).forEach((key) => {
    const bucketData = data[key as keyof typeof data];
    if (!bucketData || !bucketData.propulsion_efficiency_lifetime) {
      return;
    }
    const propulsionEfficiencyLifetime = bucketData.propulsion_efficiency_lifetime;
    const reportNumbers = bucketData.report_number;
    const mapped = propulsionEfficiencyLifetime.map(([timestamp, value], index) => {
      return {
        x: timestamp as number,
        y: value as number,
        reportNumber: reportNumbers ? reportNumbers[index][1] : undefined,
      } as MapPoint;
    });

    result[key] = [
      {
        name: 'Propulsion Efficiency',
        data: mapped,
        tooltipConfig: [
          { key: 'reportNumber', label: 'Report Number' },
          { key: 'y', label: 'Propulsion Efficiency (%)' },
        ],
      },
    ];
  });
  return result;
}

function createSfocData(data: DashboardBreakdownByDateRange, selectedRange?: DateRangeBucketKey) {
  const result: Record<string, ScatterGraphData> = {};
  getBucketKeys(data, selectedRange).forEach((key) => {
    const bucketData = data[key as keyof typeof data];
    if (!bucketData || !bucketData.sfoc_lifetime) {
      return;
    }
    const sfocLifetime = bucketData.sfoc_lifetime;
    const reportNumbers = bucketData.report_number;
    const bunkerPorts = bucketData.bunker_port;
    const bunkerDates = bucketData.bunker_date;
    const fuelGrades = bucketData.fuel_grade_in_use_main_engine;
    const tankNames = bucketData.name;
    const meReportedCons = bucketData.fuel_series;
    const meModelCons = bucketData.model_fuel;
    const meSfocLcvCorr = bucketData.sfoc_lcv_corrected;
    const meModelSfoc = bucketData.model_sfoc;

    const mapped = sfocLifetime.map(([timestamp, value], index) => {
      return {
        x: timestamp as number,
        y: value as number,
        reportNumber: reportNumbers ? reportNumbers[index][1] : undefined,
        bunkerPort: bunkerPorts ? bunkerPorts[index][1] : undefined,
        bunkerDate: bunkerDates ? bunkerDates[index][1] : undefined,
        fuelGrade: fuelGrades ? fuelGrades[index][1] : undefined,
        tankName: tankNames ? tankNames[index][1] : undefined,
        meReportedCons: meReportedCons ? meReportedCons[index][1] : undefined,
        meModelCons: meModelCons ? meModelCons[index][1] : undefined,
        meSfocLcvCorr: meSfocLcvCorr ? meSfocLcvCorr[index][1] : undefined,
        meModelSfoc: meModelSfoc ? meModelSfoc[index][1] : undefined,
      } as MapPoint;
    });

    const groupedByFuelType = mapped.reduce((acc: Record<string, MapPoint[]>, point) => {
      const fuelGrade: string = point['fuelGrade'] as string;
      if (!acc[fuelGrade]) {
        acc[fuelGrade] = [];
      }
      acc[fuelGrade].push(point);
      return acc;
    }, {});

    result[key] = Object.keys(groupedByFuelType).map((fuelGrade) => {
      return {
        name: `ME SFOC LCV Corr. Dev. (%) - ${fuelGrade}`,
        data: groupedByFuelType[fuelGrade],
      };
    });
  });
  return result;
}

function createSpeedPerformanceData(
  data: DashboardBreakdownByDateRange,
  dryDockingResult: CreateDryDockingResult | undefined,
  selectedRange?: DateRangeBucketKey
): Record<string, ScatterGraphData> {
  const result: Record<string, ScatterGraphData> = {};

  getBucketKeys(data, selectedRange).forEach((key) => {
    const bucketData = data[key as keyof typeof data];
    if (!bucketData || !bucketData.speed_performance_lifetime || bucketData.speed_performance_lifetime.length === 0) {
      return;
    }

    const speedPerformanceLifetime = [...bucketData.speed_performance_lifetime].sort(
      (a, b) => (a[0] as number) - (b[0] as number)
    );
    const reportNumbers = bucketData.report_number;
    const startDate = speedPerformanceLifetime[0][0] as number;
    const endDate = speedPerformanceLifetime[speedPerformanceLifetime.length - 1][0] as number;

    const spMapped = speedPerformanceLifetime.map(([timestamp, value], index) => {
      return {
        x: timestamp as number,
        y: value as number,
        reportNumber: reportNumbers ? reportNumbers[index][1] : undefined,
      } as MapPoint;
    });

    const dryDockTrendSeries =
      dryDockingResult?.items
        .filter((item) => item.plotline.value >= startDate && item.plotline.value <= endDate)
        .flatMap((item) => {
          const plotline = item.plotline;
          const meta = item.meta;
          const startX = spMapped[0]?.x;
          const endX = spMapped[spMapped.length - 1]?.x;
          const beforeValue = meta.speedPerformanceBefore;
          const afterValue = meta.speedPerformanceAfter;
          const changeValue = meta.speedPerformanceChange;
          const beforeDuration = meta.speedPerformanceBeforeDays;
          const beforeReports = meta.speedPerformanceBeforeReports;
          const afterDuration = meta.speedPerformanceAfterDays;
          const afterReports = meta.speedPerformanceAfterReports;

          if (startX === undefined || endX === undefined || beforeValue === undefined || afterValue === undefined) {
            return [];
          }
          const eventName = meta.isPropellerPolish ? 'Propeller Polishing' : 'Hull Cleaning';
          const tooltipConfig = [
            { key: 'y', label: 'Mean' },
            { key: 'change', label: 'Change (%)' },
            { key: 'duration', label: 'Duration (days)' },
            { key: 'reports', label: 'Reports' },
          ];
          return [
            {
              name: `Before ${eventName}`,
              onlyTrendLineVisible: true,
              tooltipConfig,
              data: [
                {
                  x: startX,
                  y: roundToPlaces(beforeValue, 2),
                  change: roundToPlaces(changeValue ?? 0, 2),
                  duration: beforeDuration,
                  reports: beforeReports,
                },
                {
                  x: plotline.value,
                  y: roundToPlaces(beforeValue, 2),
                  change: roundToPlaces(changeValue ?? 0, 2),
                  duration: beforeDuration,
                  reports: beforeReports,
                },
              ],
            },
            {
              name: `After ${eventName}`,
              onlyTrendLineVisible: true,
              tooltipConfig,
              data: [
                {
                  x: plotline.value,
                  y: roundToPlaces(afterValue, 2),
                  change: roundToPlaces(changeValue ?? 0, 2),
                  duration: afterDuration,
                  reports: afterReports,
                },
                {
                  x: endX,
                  y: roundToPlaces(afterValue, 2),
                  change: roundToPlaces(changeValue ?? 0, 2),
                  duration: afterDuration,
                  reports: afterReports,
                },
              ],
            },
          ];
        }) ?? [];

    result[key] = [
      {
        name: 'Speed Performance',
        negativeColor: 'rgba(255, 99, 132, 0.7)',
        tooltipConfig: [
          { key: 'reportNumber', label: 'Report Number' },
          { key: 'y', label: 'Speed Performance (%)' },
        ],
        data: spMapped,
      },
      ...dryDockTrendSeries,
    ];
  });

  return result;
}

function getMinMaxBoundaries(ciiMin: number, ciiMax: number, limits: LimitData) {
  const upperPadding = Math.abs(limits.lowerMid - limits.top);
  const lowerPadding = Math.abs(limits.bottom - limits.upperMid);
  let minBoundary = limits.top - upperPadding;
  let maxBoundary = limits.bottom + lowerPadding;

  if (ciiMax > maxBoundary) maxBoundary = ciiMax * 1.2;
  if (ciiMin < minBoundary) minBoundary = ciiMin * 0.8;

  return { minBoundary, maxBoundary };
}

function getMinMaxAttainedCii(ciiAttained: DateValueIndex) {
  const attained = Object.values(ciiAttained);
  return {
    ciiMin: getMin(attained),
    ciiMax: getMax(attained),
  };
}

function getCiiLimits(ciiLimits: DateValueIndex[], date: number): LimitData {
  return {
    top: ciiLimits[0][date],
    lowerMid: ciiLimits[1][date],
    upperMid: ciiLimits[2][date],
    bottom: ciiLimits[3][date],
  };
}

function gatherBoundaryData(ciiLimits: DateValueIndex[], min: number, max: number, date: number, average: number) {
  const limits = getCiiLimits(ciiLimits, date);
  const ciiMin = getMin([min, average]);
  const ciiMax = getMax([max, average]);
  const { minBoundary, maxBoundary } = getMinMaxBoundaries(ciiMin, ciiMax, limits);
  return { limits, minBoundary, maxBoundary };
}
